import React from 'react';

import { Page, Report as PowerBIReport } from 'powerbi-client';
import ExcelModel from './model';

import { Market } from '../MarketEditor/interfaces';
import { Adjustment } from '../AdjustmentsEditor/interfaces';
import { useAuthStore } from '../../../../features/authentication';

interface ButtonProps {
  reportRef: React.MutableRefObject<PowerBIReport | null>;
  selectedCompany: string;
  markets: Market[];
  adjustments: Adjustment[];
  discountDisplay: string;
  paintProvided: string;
  selectedSet: string;
  exportName: string;
}
const ExcelExportButton: React.FC<ButtonProps> = ({
  reportRef,
  selectedCompany,
  markets,
  adjustments,
  discountDisplay,
  paintProvided,
  selectedSet,
  exportName,
}) => {
  const { username } = useAuthStore();
  const [isLoading, setIsLoading] = React.useState(false);

  const exportVisualData = async () => {
    setIsLoading(true);

    if (!reportRef.current) {
      setIsLoading(false);

      console.error('Report container not found.');
      return;
    }

    const report = reportRef.current;
    if (!report) {
      setIsLoading(false);

      console.error('Report not found.');
      return;
    }

    try {
      const pages: Page[] = await report.getPages();

      let activePages = pages.filter((page: Page) => page.isActive);

      let table = new ExcelModel();

      const allExportPromises = [] as Promise<any>[];

      for (const page of activePages) {
        const visuals = await page.getVisuals();

        for (const visual of visuals) {
          if (visual.type === 'tableEx' || visual.type === 'card' || visual.type === 'cardVisual') {
            allExportPromises.push(table.maybeAddTable(visual));
          }
          if (visual.type === 'slicer') {
            allExportPromises.push(table.maybeAddSlicer(visual));
          }
        }
      }

      // Wait for all export promises to resolve
      await Promise.all(allExportPromises);

      table.ReportName = 'Full_Blended';
      table.User = username;
      table.Date_Time_UTC = getCurrentUTCDateTime();
      table.CompanyName = selectedCompany;
      table.MarketsQty = markets.length.toString();
      table.UnitsQty = markets.reduce((acc, market) => acc + market.units, 0).toString();
      table.MarketsUnitsList = markets.map(market => `{${market.market}:${market.units}}`).join(',');
      table.PaintProvided = paintProvided;
      table.AdjustmentsSetStatus = selectedSet ? 'On' : 'Off';
      table.AdjustmentsSetName = selectedSet;
      table.PriceCustomAdjustments = adjustments
        .map(
          adjustment =>
            `{Floorplan:${adjustment.config},Parameter:${adjustment.name},Type:${adjustment.type},Value:${adjustment.value}}`
        )
        .join(',');

      table.ClubhouseAmenityCleaning =
        '{Activity Rooms (EA):50},{Clubhouse / Leasing Office:175},{Fitness:75},{Laundry Rooms (EA):50},{Model:55}';

      table.export(exportName);
    } catch (error) {
      console.error('Error exporting visual data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button onClick={exportVisualData} style={{ ...styles.button }} disabled={isLoading}>
        {isLoading ? 'Exporting...' : 'Export as Excel'}
      </button>
    </div>
  );
};

const styles = {
  button: {
    height: '5.6vh',
    width: '24vh',
    fontSize: '1.6vh',
    fontWeight: '700',
    color: '#ffffff',
    backgroundColor: 'rgba(0, 171, 155, 1)', // Blue color
    border: 'none',
    borderRadius: '1.6vh',
    cursor: 'pointer',
    transition: 'background-color 0.3s, transform 0.2s',
    outline: 'none',
  },
  buttonHover: {
    backgroundColor: '#218838', // Darker green for hover
    transform: 'translateY(-0.2vh)',
  },
};

function getCurrentUTCDateTime(): string {
  const now = new Date();

  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export default ExcelExportButton;
